import { apiClient } from '@indipia/shared';
import type { ApiResponse } from '@indipia/shared-types';
import type { ExternalLinkSendPayload, ExternalLinkSendResult } from '@indipia/shared-features';

// 스케줄(일정공유·반장배정) 공유 발송 — 10-Indipia god-BFF가 아니라 sv-schedule이 직접
// 오케스트레이션한다(스케줄발송이관 v1.0 W5-a). 사진 sendPhotoShare와 동형.
// KakaoShareView는 <ExternalLinkSend>에 sendOverride로 주입하고, ShareHistoryView의
// 일정공유 재발송도 이 함수를 직접 호출한다. 시공반장 일정확인(FOREMAN_CONFIRM)은 이 경로를
// 쓰지 않는다(서버가 UNSUPPORTED_SHARE_TYPE 차단) — sendForemanConfirm 사용.
// 고정 BE 계약(POST /schedule/shares/send): body에 channel·sourceAgent 없음 —
// notiType은 BE가 shareType(SCHEDULE_SHARE)으로 결정한다.
// 응답에도 channel 없음(D4 — 발신 이력 CHANNEL_TYPE은 발송 시점 null).
// ── 시공반장 일정확인 발송 (POST /schedule/tasks/{taskId}/foreman-confirm-sends) ──
// 서버가 이전 요청 마감·링크 발급·알림톡 발송·이력 기록·상태 전이를 한 번에 수행한다.
// FE는 직접 발송(shares/send)·이력 기록(share-history POST)을 하지 않는다(서버가 거절).
// 방식: MANUAL(partnerId 선택 — 다른 반장 지정 발송) · RESEND(partnerId 필수 — 현재 반장과 같을 때만 발송, 공종·반장별 1번). AUTO는 서버 내부 전용.
// CHANGE(변경 카톡)는 /schedules/{id}/schedule-change-notices 로 별도 처리.
export type ForemanSendMethod = 'MANUAL' | 'RESEND';

export interface ForemanConfirmSendRequest {
  sendMethod: ForemanSendMethod;
  /** RESEND: 필수 — 다시 보낼 대상 반장. 서버가 공종의 현재 반장과 같을 때만 발송하고 다르면 422 SEND_NOT_ALLOWED.
   *  MANUAL: 다른 반장을 골라 보낼 때 지정하며, 성공 시 그 반장으로 지정된다. */
  partnerId?: number;
}

export interface ForemanConfirmSendResult {
  taskId: number;
  historyId: number;
  attemptNo: number;
  sendMethod: string;
  partnerId: number;
  partnerStatus: string;
  sentDtm: string;
  replyExpireDtm: string | null;
  closedRequests: { historyId: number; partnerId: number | null; closedReason: string; noticed: boolean }[];
}

/** 서버 오류 코드(ErrorCode) 중 발송 API가 돌려주는 것. 그 외는 UNKNOWN. */
export type ForemanSendErrorCode =
  | 'FOREMAN_CONTACT_MISSING'
  | 'FOREMAN_CONFIRM_SEND_FAILED'
  | 'FOREMAN_NOT_ASSIGNED'
  | 'SEND_NOT_ALLOWED'
  | 'RESEND_LIMIT_EXCEEDED'
  | 'CONFLICT'
  | 'INVALID_REQUEST'
  | 'UNKNOWN';

const FOREMAN_SEND_ERROR_CODES: readonly string[] = [
  'FOREMAN_CONTACT_MISSING', 'FOREMAN_CONFIRM_SEND_FAILED', 'FOREMAN_NOT_ASSIGNED',
  'SEND_NOT_ALLOWED', 'RESEND_LIMIT_EXCEEDED', 'CONFLICT', 'INVALID_REQUEST',
];

/** 발송 API 오류 — 422 FOREMAN_CONTACT_MISSING·502 FOREMAN_CONFIRM_SEND_FAILED는 이력에 실패로 기록된 건이며 failReason·partnerStatus가 채워진다. */
export class ForemanConfirmSendError extends Error {
  readonly code: ForemanSendErrorCode;
  readonly httpStatus: number | null;
  readonly failReason: string | null;
  readonly partnerStatus: string | null;
  constructor(code: ForemanSendErrorCode, httpStatus: number | null, failReason: string | null, partnerStatus: string | null) {
    super(code);
    this.name = 'ForemanConfirmSendError';
    this.code = code;
    this.httpStatus = httpStatus;
    this.failReason = failReason;
    this.partnerStatus = partnerStatus;
  }
}

function toForemanSendError(err: unknown): ForemanConfirmSendError {
  const res = (err as { response?: { status?: number; data?: { code?: string; data?: { failReason?: string; partnerStatus?: string } | null } } })?.response;
  const code = res?.data?.code;
  const known = code && FOREMAN_SEND_ERROR_CODES.includes(code) ? (code as ForemanSendErrorCode) : 'UNKNOWN';
  return new ForemanConfirmSendError(known, res?.status ?? null, res?.data?.data?.failReason ?? null, res?.data?.data?.partnerStatus ?? null);
}

export const scheduleApi = {
  /** 시공반장 일정확인 발송 1건(공종 단위). 실패 시 {@link ForemanConfirmSendError}를 던진다. */
  async sendForemanConfirm(taskId: number, req: ForemanConfirmSendRequest): Promise<ForemanConfirmSendResult> {
    try {
      const res = await apiClient.post<ApiResponse<ForemanConfirmSendResult>>(
        `/schedule/tasks/${taskId}/foreman-confirm-sends`,
        {
          sendMethod: req.sendMethod,
          ...(req.partnerId != null && { partnerId: req.partnerId }),
        },
      );
      return res.data.data;
    } catch (err) {
      throw toForemanSendError(err);
    }
  },

  async sendScheduleShare(payload: ExternalLinkSendPayload): Promise<ExternalLinkSendResult> {
    // recipients.mobile은 호출부(useExternalLink toApiRecipient / 직접호출 시 replace)에서
    // 이미 하이픈이 제거된 순수 숫자다.
    const body = {
      sourceService: payload.sourceService,
      resourceType:  payload.resourceType,
      resourceRef:   payload.resourceRef,
      shareType:     payload.shareType,
      shareScope:    payload.shareScope,
      authType:      payload.authType,
      // 유효기간은 서버가 산정한다 — 입력 폐지 화면(스케줄 공유)은 빈 값이라 보내지 않는다.
      ...(payload.expiresAt && { expiresAt: payload.expiresAt }),
      variables:     payload.variables,
      ...(payload.dispatchBatchId && { dispatchBatchId: payload.dispatchBatchId }),
      recipients:    payload.recipients,
      ...(payload.buttons && payload.buttons.length > 0 && { buttons: payload.buttons }),
    };
    const res = await apiClient.post<ApiResponse<ExternalLinkSendResult>>(
      '/schedule/shares/send',
      body,
    );
    const data = res.data.data;
    return {
      shareBatchId:    data.shareBatchId,
      dispatchBatchId: data.dispatchBatchId,
      sentCount:       data.sentCount,
      failedCount:     data.failedCount,
      results:         data.results,
    };
  },
};

/**
 * 이미 스케줄이 있는 현장 번호 전체 — 현장 선택 화면의 제외 대상.
 * 스케줄 목록을 한 번(200건)만 받으면 200건을 넘는 사업장에서 나머지 현장이 제외되지 않으므로
 * hasNext가 끝날 때까지 쪽을 이어서 받는다(서버 응답: schedules·hasNext).
 */
export async function fetchScheduledSiteIds(compId: number): Promise<number[]> {
  const PAGE_SIZE = 200;
  const MAX_PAGES = 100; // 비정상 응답에서의 무한 반복 방지(2만 건)
  const siteIds = new Set<number>();
  for (let page = 0; page < MAX_PAGES; page++) {
    const res = await apiClient.get<{ data: { schedules: { siteId: number }[]; hasNext?: boolean } }>(
      '/schedule/schedules',
      { params: { page, pageSize: PAGE_SIZE }, headers: { 'X-Comp-Id': String(compId) } },
    );
    for (const s of res.data.data?.schedules ?? []) siteIds.add(s.siteId);
    if (!res.data.data?.hasNext) break;
  }
  return [...siteIds];
}
